import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { AsyncButton, Button } from '@components/button';
import { Form, FormSelect } from '@components/form';
import { Channel, useCreateRemoteDevelopmentMutation } from '@generated/UseGraphqlHooks';
import { MenuItem, Stack, Typography } from '@mui/material';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import * as yup from 'yup';

const GET_USER_ORGS = gql`
  query GetUserOrgs {
    getOrganizations {
      expired
      name
      organizationId
    }
  }
`;

const GET_ORG_CHANNELS = gql`
  query GetOrgChannels($organizationId: String) {
    getChannels(organizationId: $organizationId) {
      channelId
      name
      organizationId
      state
      version
    }
  }
`;

const validationSchema = yup.object({
  organizationId: yup.string().required('Organization id is required'),
  channelId: yup.string().required('Channel id is required'),
});

export const CreateRemoteSessionModal = ({ onClose }: { onClose: VoidFunction }) => {
  const { data } = useQuery(GET_USER_ORGS);
  const [getOrgChannels] = useLazyQuery(GET_ORG_CHANNELS);
  const [createSession] = useCreateRemoteDevelopmentMutation();
  const [orgChannels, setOrgChannels] = useState<Channel[] | null>(null);
  const handleFormSubmit = async (values: { channelId: string; organizationId: string }) => {
    posthog.capture('user_create_remote-session', { channelId: values.channelId });
    await createSession({
      variables: { channelId: values.channelId, organizationId: values.organizationId },
    });
    onClose();
  };

  return (
    <Form
      initialValues={{
        organizationId: '',
        channelId: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange={true}
    >
      {({ isValid, handleSubmit, dirty, isSubmitting, values }) => {
        useEffect(() => {
          if (values.organizationId) {
            getOrgChannels({ variables: { organizationId: values.organizationId } }).then((res) => {
              setOrgChannels(
                res.data?.getChannels.filter(
                  (channel) =>
                    channel.organizationId === values.organizationId &&
                    (channel.state === 'ready' ||
                      (channel.state === 'deploying' && parseInt(channel.version) >= 1)),
                ) || [],
              );
            });
          }
        }, [values.organizationId]);
        return (
          <Stack>
            <FormSelect name="organizationId" label="Organization *">
              {data?.getOrganizations
                .filter((org) => !org.expired)
                .map((org) => (
                  <MenuItem key={org.organizationId} value={org.organizationId}>
                    {org.name}
                  </MenuItem>
                ))}
            </FormSelect>
            {values.organizationId && orgChannels?.length > 0 ? (
              <FormSelect name="channelId" label="Channel *">
                {orgChannels.map((channel) => (
                  <MenuItem key={channel.channelId} value={channel.channelId}>
                    {channel.name}
                  </MenuItem>
                ))}
              </FormSelect>
            ) : values.organizationId && orgChannels?.length === 0 ? (
              <Typography variant="body2" sx={{ mb: 4, maxWidth: '60ch' }}>
                This organization has no channels available for remote development. Please deploy a
                channel or select a different organization.
              </Typography>
            ) : null}
            <Stack gap={4}>
              <AsyncButton
                fullWidth
                loading={isSubmitting}
                disabled={!(isValid && dirty && !isSubmitting)}
                onClick={handleSubmit}
                data-cy="New-Workspace-Modal-Create-Button"
              >
                Create
              </AsyncButton>
              <Button
                fullWidth
                variant="secondary"
                data-cy="New-Workspace-Modal-Cancel-Button"
                onClick={onClose}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};
